import React, { useState, useEffect } from "react";
import { Table, Form, Card, Spinner, Modal } from "react-bootstrap";
import "bootstrap-icons/font/bootstrap-icons.css";
import AppButton from "../AppButton";
import { toast } from "react-toastify";
import { apiAxios } from "../../utils/apiAxios";

const AddRestoration: React.FC = () => {
  const [restorationName, setRestorationName] = useState("");
  const [restorations, setRestorations] = useState<
    { restorationTypeID: number; name: string }[]
  >([]);
  const [editId, setEditId] = useState<number | null>(null);
  const [editValue, setEditValue] = useState("");
  const [loading, setLoading] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/RestorationTypes`;

  const fetchRestorations = async () => {
    try {
      setLoading(true);
      const res = await apiAxios.get(`${API_BASE_URL}/GetAll`);
      if (res.data.isSuccess) {
        const restorations = res.data.singleData || [];
        setRestorations(restorations);
      } else {
        toast.warn("Failed to fetch restorations");
      }
    } catch (error) {
      console.warn("Error fetching restorations:", error);
      toast.error("Error fetching restorations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRestorations();
  }, []);

  const handleAdd = async () => {
    if (!restorationName.trim()) {
      toast.warn("Please enter a restoration name");
      return;
    }

    const isDuplicate = restorations.some(
      (r) => r.name.toLowerCase() === restorationName.trim().toLowerCase()
    );
    if (isDuplicate) {
      toast.error("Restoration with this name already exists");
      return;
    }

    try {
      const res = await apiAxios.post(`${API_BASE_URL}/Create`, {
        name: restorationName.trim(),
        createdBy: 1,
        createdOn: new Date().toISOString(),
        isActive: true,
      });

      if (res.data.isSuccess) {
        setRestorations([...restorations, res.data.singleData]);
        setRestorationName("");
        toast.success("Restoration added successfully");
      } else {
        toast.error(res.data.status || "Failed to add restoration");
      }
    } catch (error) {
      console.warn("Error adding restoration:", error);
      toast.error("Error adding restoration");
    }
  };

  const confirmDelete = (id: number) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (deleteId === null) return;

    try {
      const res = await apiAxios.delete(`${API_BASE_URL}/Delete/${deleteId}`);
      if (res.data.isSuccess) {
        setRestorations(
          restorations.filter((item) => item.restorationTypeID !== deleteId)
        );
        toast.success("Restoration deleted successfully");
      } else {
        toast.error(res.data.status || "Failed to delete restoration");
      }
    } catch (error) {
      console.warn("Error deleting restoration:", error);
      toast.error("Error deleting restoration");
    } finally {
      setDeleteId(null);
      setShowDeleteModal(false);
    }
  };

  const handleEdit = (id: number, name: string) => {
    setEditId(id);
    setEditValue(name);
  };

  const handleUpdate = async () => {
    if (!editValue.trim() || editId === null) {
      toast.warn("Please enter a restoration name");
      return;
    }

    const isDuplicate = restorations.some(
      (r) =>
        r.name.toLowerCase() === editValue.trim().toLowerCase() &&
        r.restorationTypeID !== editId
    );
    if (isDuplicate) {
      toast.error("Another restoration with this name already exists");
      return;
    }

    try {
      const res = await apiAxios.put(`${API_BASE_URL}/Update`, {
        restorationTypeID: editId,
        name: editValue.trim(),
        modifiedBy: 1,
        modifiedOn: new Date().toISOString(),
        isActive: true,
      });

      if (res.data.isSuccess) {
        setRestorations(
          restorations.map((item) =>
            item.restorationTypeID === editId
              ? { ...item, name: editValue.trim() }
              : item
          )
        );
        setEditId(null);
        setEditValue("");
        toast.success("Restoration updated successfully");
      } else {
        toast.error(res.data.status || "Failed to update restoration");
      }
    } catch (error) {
      console.warn("Error updating restoration:", error);
      toast.error("Error updating restoration");
    }
  };

  const handleCancel = () => {
    setEditId(null);
    setEditValue("");
  };

  return (

    
    <div className="container mt-4">
      <h3 className="mb-4 fw-bold">🦷 Add Restoration</h3>
      <Card className="shadow-sm mb-4 border-0">
        <Card.Body>
          <Card.Title className="mb-3">Add New Restoration</Card.Title>
          <div className="d-flex gap-2">
            <Form.Control
              type="text"
              placeholder="Enter restoration name"
              value={restorationName}
              onChange={(e) => setRestorationName(e.target.value)}
            />
            <AppButton variant="primary" size="sm" onClick={handleAdd}>
              <i className="bi bi-plus-lg"></i> Add
            </AppButton>
          </div>
        </Card.Body>
      </Card>

      <Card className="shadow-sm border-0">
        <Card.Body>
          <Card.Title className="mb-3">Restoration List</Card.Title>
          <Table bordered hover responsive className="align-middle shadow-sm mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ width: "80px" }}>Sr No</th>
                <th>Restoration</th>
                <th style={{ width: "250px" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={3} className="text-center text-muted">
                    Loading...
                  </td>
                </tr>
              ) : restorations.length === 0 ? (
                <tr>
                  <td colSpan={3} className="text-center text-muted">
                    No restorations added yet
                  </td>
                </tr>
              ) : (
                restorations.map((item, index) => (
                  <tr key={item.restorationTypeID}>
                    <td>{index + 1}</td>
                    <td>
                      {editId === item.restorationTypeID ? (
                        <Form.Control
                          type="text"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                        />
                      ) : (
                        item.name
                      )}
                    </td>
                    <td>
                      {editId === item.restorationTypeID ? (
                        <>
                          <AppButton
                            variant="success"
                            size="sm"
                            className="me-2"
                            onClick={handleUpdate}
                          >
                            <i className="bi bi-check-lg"></i> Update
                          </AppButton>
                          <AppButton
                            variant="secondary"
                            size="sm"
                            onClick={handleCancel}
                          >
                            <i className="bi bi-x-lg"></i> Cancel
                          </AppButton>
                        </>
                      ) : (
                        <>
                          <AppButton
                            variant="warning"
                            size="sm"
                            className="me-2 text-dark"
                            onClick={() =>
                              handleEdit(item.restorationTypeID, item.name)
                            }
                          >
                            <i className="bi bi-pencil-square"></i> Edit
                          </AppButton>
                          <AppButton
                            variant="danger"
                            size="sm"
                            onClick={() => confirmDelete(item.restorationTypeID)}
                          >
                            <i className="bi bi-trash"></i> Delete
                          </AppButton>
                        </>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Body>
          Are you sure you want to delete this restoration?
        </Modal.Body>
        <Modal.Footer>
          <AppButton variant="danger" onClick={handleDelete}>
            Yes
          </AppButton>
          <AppButton
            variant="secondary"
            onClick={() => setShowDeleteModal(false)}
          >
            No
          </AppButton>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AddRestoration;
